import { useState, useEffect } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Megaphone, Instagram, Linkedin, Twitter, Globe, PenTool, BarChart3, ArrowRight, CheckCircle2, Calendar, Clock, MapPin, User, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import AppLinkBanner from "@/components/AppLinkBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Outreach() {
  const [chairs, setChairs] = useState([]);
  const [img, setImg] = useState({});
  const [c, setC] = useState({});
  const [events, setEvents] = useState([]);

  useEffect(() => {
    axios.get(`${API}/team`).then(r => {
      setChairs(r.data.filter(m => m.category === 'Marketing Chair'));
    }).catch(() => {});
    axios.get(`${API}/site-images`).then(r => setImg(r.data)).catch(() => {});
    axios.get(`${API}/site-content`).then(r => setC(r.data)).catch(() => {});
    axios.get(`${API}/events`).then(r => setEvents(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {/* Page Header */}
      <section className="page-header">
        <div className="container-main">
          <p className="text-slate-400 text-xs font-medium uppercase tracking-widest mb-3">Committee</p>
          <h1 data-testid="outreach-page-title" className="text-4xl sm:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Outreach & Marketing</h1>
          <p className="text-slate-300 mt-4 text-base max-w-2xl">{c.outreachHeaderDesc || "Amplifying the Texas BHA brand and connecting with healthcare professionals, faculty, and industry leaders across the state."}</p>
        </div>
      </section>

      {/* Application Link */}
      <AppLinkBanner link={c.outreachAppLink} dueDate={c.outreachAppDue} testId="outreach-application" />

      {/* Mission with Photo */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-4">
              <div className="rounded-2xl overflow-hidden aspect-[4/3] bg-slate-100">
                <img src={img.outreachTeam} alt="Marketing team" className="w-full h-full object-cover" />
              </div>
              <p className="text-xs text-slate-400 text-center italic">Upload your own team photos via the admin panel</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Our Mission</p>
              <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {c.outreachMissionTitle || "Building Our Brand & Community"}
              </h2>
              <p className="text-slate-600 mt-4 leading-relaxed text-[15px]">
                {c.outreachMissionText || "The Outreach & Marketing Committee is responsible for growing the Texas BHA brand, managing our social media presence, and creating compelling content that attracts new members, sponsors, and partners."}
              </p>
              <ul className="mt-5 space-y-2.5">
                {(c.outreachBullets || "Social media strategy and content creation,Event promotion and digital marketing campaigns,Brand identity and visual standards,Newsletter production and distribution,Partnership outreach and sponsor recruitment").split(",").map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <CheckCircle2 className="h-4 w-4 text-[#0F172A] mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section data-testid="upcoming-events" className="py-16 lg:py-20 bg-slate-50">
        <div className="container-main">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">What's Next</p>
              <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Upcoming Events</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {events.map((event, i) => (
              <Card key={event.id || i} data-testid={`event-card-${i}`} className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all duration-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-[#0F172A] flex flex-col items-center justify-center shrink-0 text-white">
                      <span className="text-[10px] font-medium uppercase leading-none">{event.date.split(' ')[0]}</span>
                      <span className="text-lg font-bold leading-tight">{event.date.split(' ')[1]?.replace(',', '')}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#0F172A] text-[15px] leading-snug" style={{ fontFamily: 'Manrope, sans-serif' }}>{event.title}</h3>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                        <span className="flex items-center gap-1.5 text-xs text-slate-400">
                          <Clock className="h-3 w-3" />{event.time}
                        </span>
                        <span className="flex items-center gap-1.5 text-xs text-slate-400">
                          <MapPin className="h-3 w-3" />{event.location}
                        </span>
                      </div>
                      <p className="text-slate-500 text-sm mt-2 leading-relaxed">{event.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {events.length === 0 && (
              <div className="col-span-full text-center py-8 bg-white rounded-xl border border-dashed border-slate-200">
                <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">No upcoming events</p>
                <p className="text-slate-400 text-xs mt-1">Add events via the admin panel</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Google Calendar Link */}
      <section data-testid="google-calendar" className="py-12 bg-white border-b border-slate-100">
        <div className="container-main">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 p-6 bg-slate-50 rounded-2xl border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#0F172A] flex items-center justify-center shrink-0">
                <Calendar className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-[15px] text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>BHA Event Calendar</h3>
                <p className="text-slate-500 text-sm mt-0.5">View all upcoming events, meetings, and deadlines in one place.</p>
              </div>
            </div>
            <a
              href="https://calendar.google.com/calendar"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="calendar-link"
            >
              <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-full px-6 text-sm font-semibold whitespace-nowrap">
                Open Calendar <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Separator className="bg-slate-100" />

      {/* Social Links */}
      <section className="py-12 bg-slate-50">
        <div className="container-main text-center">
          <h3 className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>Follow Texas BHA</h3>
          <div className="flex justify-center gap-3 mt-5">
            {[
              { icon: Instagram, href: "https://instagram.com/tx_bha", label: "Instagram" },
              { icon: Linkedin, href: "https://linkedin.com/company/texasbha", label: "LinkedIn" },
              { icon: Twitter, href: "https://twitter.com/texasbha", label: "X / Twitter" },
            ].map((s, i) => (
              <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" data-testid={`outreach-social-${i}`}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all text-sm font-medium text-slate-600 hover:text-[#0F172A]">
                <s.icon className="h-4 w-4" />
                {s.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Grid */}
      <section className="py-12 bg-white">
        <div className="container-main">
          <h3 className="text-lg font-semibold text-[#0F172A] mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>Behind the Scenes</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[img.outreachGallery1, img.outreachGallery2, img.outreachGallery3, img.outreachGallery4].filter(Boolean).map((src, i) => (
              <div key={i} className="aspect-square rounded-xl overflow-hidden bg-slate-100 relative group">
                <img src={src} alt={`Marketing ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 text-center mt-3 italic">Upload your own team and event photos via the admin panel</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-slate-50 border-t border-slate-100">
        <div className="container-main text-center">
          <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>Join Our Marketing Team</h2>
          <p className="text-slate-500 mt-2 max-w-md mx-auto text-sm">Creative, strategic, and passionate about healthcare? We'd welcome you on the team.</p>
          <Link to="/contact" data-testid="outreach-cta">
            <Button className="mt-5 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-full px-7 text-sm font-semibold">
              Get Involved <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
